import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import {
  Users,
  Plus,
  Mail,
  Shield,
  UserPlus,
  Settings,
  Crown,
  Eye,
  Edit,
  X,
  Send,
  Trash2,
  UserX,
  AlertCircle
} from 'lucide-react';
import { getProjects, getTeamMembers, inviteTeamMember, removeTeamMember, updateMemberRole, cancelInvitation, TeamMember, TeamInvitation, TeamData, Project } from '../services/teamApi';
import { PermissionsProvider, usePermissions } from '../contexts/PermissionsContext';
import { useToast } from '../contexts/ToastContext';

interface ProjectTeamData {
  project: Project;
  teamData: TeamData;
}

interface TeamContentProps {
  externalSelectedProject: string | null;
  onProjectChange: (projectId: string) => void;
}

const roleOptions = [
  { value: 'admin', label: 'Administrador' },
  { value: 'member', label: 'Membro' },
  { value: 'viewer', label: 'Visualizador' }
];

/**
 * TeamContent Component
 *
 * Manages team members and invitations for projects.
 *
 * Role Editing:
 * - Owners can edit member roles via inline Select dropdown
 * - Roles: admin, member, viewer
 * - Owner role is permanent and cannot be edited
 * - Changes are immediate with auto-refresh on success
 * - Error messages displayed on failure
 */
const TeamContent: React.FC<TeamContentProps> = ({ externalSelectedProject, onProjectChange }) => {
  const { canManage } = usePermissions();
  const canManageTeam = canManage('team');
  const { showSuccess } = useToast();

  const [projects, setProjects] = useState<Project[]>([]);
  const [projectTeams, setProjectTeams] = useState<Map<string, TeamData>>(new Map());
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      setError(null);

      // Buscar projetos do usuário
      const projectsData = await getProjects();
      setProjects(projectsData);

      // Buscar dados da equipe para cada projeto
      const teamsMap = new Map<string, TeamData>();
      
      for (const project of projectsData) {
        try {
          const teamData = await getTeamMembers(project._id);
          teamsMap.set(project._id, teamData);
        } catch (error) {
          console.error(`Error fetching team data for project ${project._id}:`, error);
          // Continuar mesmo se falhar para um projeto específico
        }
      }

      setProjectTeams(teamsMap);
      
      // Selecionar o primeiro projeto se houver
      if (projectsData.length > 0 && !externalSelectedProject) {
        onProjectChange(projectsData[0]._id);
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
      setError('Erro ao carregar dados da equipe. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail || !externalSelectedProject) return;

    setInviting(true);
    setError(null);

    try {
      const result = await inviteTeamMember(externalSelectedProject, inviteEmail, inviteRole);

      // Resetar form
      setInviteEmail('');
      setInviteRole('member');
      setShowInviteModal(false);

      // Mostrar aviso se o email não foi enviado
      if (result && !result.emailSent) {
        setError('⚠️ Convite criado, mas o email não pôde ser enviado. Verifique a configuração de email ou compartilhe o link manualmente.');
      }

      // Recarregar dados da equipe do projeto atual
      await refreshProjectTeam(externalSelectedProject);
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      setError(error.response?.data?.message || 'Erro ao enviar convite. Tente novamente.');
    } finally {
      setInviting(false);
    }
  };

  const refreshProjectTeam = async (projectId: string) => {
    try {
      const teamData = await getTeamMembers(projectId);
      setProjectTeams(prev => new Map(prev).set(projectId, teamData));
    } catch (error) {
      console.error('Error refreshing team data:', error);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!externalSelectedProject) return;

    if (window.confirm('Tem certeza que deseja remover este membro?')) {
      try {
        await removeTeamMember(externalSelectedProject, userId);
        await refreshProjectTeam(externalSelectedProject);
      } catch (error: any) {
        console.error('Error removing member:', error);
        setError(error.response?.data?.message || 'Erro ao remover membro. Tente novamente.');
      }
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!externalSelectedProject) return;

    if (window.confirm('Tem certeza que deseja cancelar este convite?')) {
      try {
        await cancelInvitation(externalSelectedProject, invitationId);
        await refreshProjectTeam(externalSelectedProject);
      } catch (error: any) {
        console.error('Error cancelling invitation:', error);
        setError(error.response?.data?.message || 'Erro ao cancelar convite. Tente novamente.');
      }
    }
  };

  const handleUpdateRole = async (userId: string, newRole: 'admin' | 'member' | 'viewer') => {
    if (!externalSelectedProject) return;

    setError(null);
    setUpdatingUserId(userId);

    try {
      await updateMemberRole(externalSelectedProject, userId, newRole);
      await refreshProjectTeam(externalSelectedProject);
      showSuccess('Papel atualizado com sucesso!');
    } catch (error: unknown) {
      console.error('Error updating member role:', error);
      const message = error instanceof Error && typeof error === 'object' && error !== null && 'response' in error
        ? ((error as any).response?.data?.message || 'Erro ao atualizar papel do membro.')
        : 'Erro ao atualizar papel do membro.';
      setError(message);
    } finally {
      setUpdatingUserId(null);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'admin':
        return <Shield className="w-4 h-4 text-blue-500" />;
      case 'member':
        return <Edit className="w-4 h-4 text-green-500" />;
      case 'viewer':
        return <Eye className="w-4 h-4 text-gray-500" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return 'Proprietário';
      case 'admin':
        return 'Administrador';
      case 'member':
        return 'Membro';
      case 'viewer':
        return 'Visualizador';
      default:
        return role;
    }
  };

  const selectedProjectData = projects.find(p => p._id === externalSelectedProject);
  const selectedTeam = externalSelectedProject ? projectTeams.get(externalSelectedProject) : null;

  if (loading) {
    return (
      <Layout currentPage="team">
        <div className="h-full flex items-center justify-center">
          <LoadingSpinner message="Carregando equipes" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPage="team">
      <div className="h-full overflow-y-auto custom-scrollbar">
        <div className="p-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground-primary mb-2 neon-blue">
              Equipe
            </h1>
            <p className="text-foreground-tertiary">
              Gerencie membros e permissões dos seus projetos
            </p>
          </div>

          {error && (
            <Card variant="glass" className="w-full mb-6">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-red-400">
                  <AlertCircle className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {projects.length === 0 ? (
            <Card variant="glass" className="w-full text-center p-12 card-hover">
              <CardContent className="space-y-6">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center">
                  <Users className="w-10 h-10 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground-primary mb-2">
                    Nenhuma equipe encontrada
                  </h2>
                  <p className="text-foreground-tertiary">
                    Crie um projeto para começar a gerenciar sua equipe
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Project Selector */}
              <div className="flex items-center gap-4">
                <Label className="text-foreground-secondary">Projeto:</Label>
                <Select
                  value={externalSelectedProject || ''}
                  onChange={onProjectChange}
                  options={projects.map(project => ({
                    value: project._id,
                    label: project.name
                  }))}
                  placeholder="Selecione um projeto"
                  className="w-80"
                />
                
                {selectedTeam && canManageTeam && (selectedTeam.userRole === 'owner' || selectedTeam.userRole === 'admin') && (
                  <Button
                    variant="gradient"
                    onClick={() => setShowInviteModal(true)}
                    className="ml-auto"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Convidar Membro
                  </Button>
                )}
              </div>

              {selectedTeam && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Members */}
                  <Card variant="glass" className="card-hover">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Membros ({selectedTeam.members.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {selectedTeam.members.map(member => (
                        <div key={member._id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center">
                              {member.userId.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-foreground-primary">{member.userId.name}</p>
                              <p className="text-sm text-foreground-tertiary">{member.userId.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getRoleIcon(member.role)}

                            {canManageTeam && selectedTeam.userRole === 'owner' && member.role !== 'owner' ? (
                              <Select
                                value={member.role}
                                onChange={(newRole: string) => handleUpdateRole(member.userId._id, newRole as 'admin' | 'member' | 'viewer')}
                                options={roleOptions}
                                className="w-40"
                                disabled={updatingUserId === member.userId._id}
                              />
                            ) : (
                              <span className="text-sm text-foreground-secondary">
                                {getRoleLabel(member.role)}
                              </span>
                            )}

                            {canManageTeam && selectedTeam.userRole === 'owner' && member.role !== 'owner' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveMember(member.userId._id)}
                                className="ml-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <UserX className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Pending Invitations */}
                  <Card variant="glass" className="card-hover">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        Convites Pendentes ({selectedTeam.invitations.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {selectedTeam.invitations.length === 0 ? (
                        <p className="text-foreground-tertiary text-center py-4">
                          Nenhum convite pendente
                        </p>
                      ) : (
                        selectedTeam.invitations.map(invitation => (
                          <div key={invitation._id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full flex items-center justify-center">
                                <Mail className="w-4 h-4 text-orange-400" />
                              </div>
                              <div>
                                <p className="font-medium text-foreground-primary">{invitation.email}</p>
                                <p className="text-sm text-foreground-tertiary">
                                  Convidado por {invitation.invitedBy.name}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {getRoleIcon(invitation.role)}
                              <span className="text-sm text-foreground-secondary">
                                {getRoleLabel(invitation.role)}
                              </span>
                              {canManageTeam && (selectedTeam.userRole === 'owner' || selectedTeam.userRole === 'admin') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCancelInvitation(invitation._id)}
                                  className="ml-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* Invite Modal */}
          {showInviteModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <Card variant="glass" className="w-full max-w-md mx-4">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Convidar Membro</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowInviteModal(false)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Digite o email do usuário"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Papel</Label>
                    <Select 
                      value={inviteRole} 
                      onChange={(value: string) => setInviteRole(value as 'admin' | 'member' | 'viewer')}
                      options={[
                        { value: 'admin', label: 'Administrador' },
                        { value: 'member', label: 'Membro' },
                        { value: 'viewer', label: 'Visualizador' }
                      ]}
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowInviteModal(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="gradient"
                      onClick={handleInviteUser}
                      loading={inviting}
                      loadingText="Enviando..."
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Enviar Convite
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

// Wrapper component that provides permissions context
const Team: React.FC = () => {
  // Need to manage selected project at wrapper level for PermissionsProvider
  const [selectedProject, setSelectedProject] = useState<string | null>(null);

  return (
    <PermissionsProvider projectId={selectedProject || undefined}>
      <TeamContent
        externalSelectedProject={selectedProject}
        onProjectChange={setSelectedProject}
      />
    </PermissionsProvider>
  );
};

export default Team;